/**
 * CompletenessTrendCard Component
 *
 * Enhanced completeness trend display with:
 * - Current completeness percentage with progress ring
 * - Detailed trend chart with area fill
 * - Multiple statistics and insights
 * - Responsive design and accessibility
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Target,
  Calendar,
  BarChart3,
  Activity,
} from 'lucide-react';
import { CompletenessTrendData } from '@/services/dashboardService';
import { useDashboardTranslations } from '@/hooks/useDashboardTranslations';

interface CompletenessTrendCardProps {
  data?: CompletenessTrendData;
  loading?: boolean;
  className?: string;
}

/**
 * Enhanced chart component with area fill and grid
 */
function EnhancedTrendChart({ data, className = '' }: { data: number[]; className?: string }) {
  const i18n = useDashboardTranslations();

  if (!data || data.length === 0) {
    return (
      <div
        className={`flex h-60 items-center justify-center rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 dark:from-muted dark:to-card ${className}`}
      >
        <div className="text-sm text-gray-400 dark:text-muted-foreground">
          {i18n.getCompletenessTrendText('noData')}
        </div>
      </div>
    );
  }

  // Filter out invalid values (NaN, null, undefined) and ensure we have valid numbers
  const validData = data.filter(
    value => typeof value === 'number' && !isNaN(value) && isFinite(value)
  );

  // If no valid data after filtering, show no data message
  if (validData.length === 0) {
    return (
      <div
        className={`flex h-60 items-center justify-center rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 dark:from-muted dark:to-card ${className}`}
      >
        <div className="text-sm text-gray-400 dark:text-muted-foreground">
          {i18n.getCompletenessTrendText('noData')}
        </div>
      </div>
    );
  }

  const max = Math.max(...validData, 100); // Ensure we show up to 100%
  const min = Math.min(...validData, 0);
  const range = max - min || 1;

  const width = 800; // Larger base width for better resolution
  const height = 240; // Increased height for better Y-axis usage
  const padding = 40;

  // Generate path points - use full width
  const points = validData.map((value, index) => {
    // Handle single data point case
    const xProgress = validData.length === 1 ? 0.5 : index / (validData.length - 1);
    const x = padding + xProgress * (width - 2 * padding);
    const y = padding + ((max - value) / range) * (height - 2 * padding);

    // Ensure coordinates are valid numbers
    const safeX = isFinite(x) ? x : padding;
    const safeY = isFinite(y) ? y : padding;

    return { x: safeX, y: safeY, value };
  });

  // Create path string for line
  const pathData = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

  // Create area path
  const areaPath = `${pathData} L ${points[points.length - 1].x} ${height - padding} L ${points[0].x} ${height - padding} Z`;

  // Determine colors based on trend
  const lastValue = validData[validData.length - 1];
  const firstValue = validData[0];
  const isPositive = lastValue >= firstValue;
  const strokeColor = isPositive ? '#10b981' : '#ef4444';
  const fillColor = isPositive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)';

  return (
    <div
      className={`relative rounded-lg bg-gradient-to-br from-gray-50 to-white p-2 dark:from-muted dark:to-card ${className}`}
    >
      <svg viewBox={`0 0 ${width} ${height}`} className="h-60 w-full" preserveAspectRatio="none">
        {/* Grid lines */}
        <defs>
          <pattern id="grid" width="40" height="20" patternUnits="userSpaceOnUse">
            <path
              d="M 40 0 L 0 0 0 20"
              fill="none"
              stroke="#f3f4f6"
              strokeWidth="1"
              className="dark:stroke-border"
            />
          </pattern>
          <pattern id="grid-dark" width="40" height="20" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 20" fill="none" stroke="hsl(220 13% 30%)" strokeWidth="1" />
          </pattern>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="url(#grid)"
          className="dark:fill-[url(#grid-dark)]"
        />

        {/* Area fill */}
        <path d={areaPath} fill={fillColor} className="opacity-60" />

        {/* Main line */}
        <path
          d={pathData}
          fill="none"
          stroke={strokeColor}
          strokeWidth="3"
          className="drop-shadow-sm"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points */}
        {points.map((point, index) => (
          <g key={index}>
            <circle
              cx={point.x}
              cy={point.y}
              r="4"
              fill="white"
              stroke={strokeColor}
              strokeWidth="2"
              className="drop-shadow-sm dark:fill-card"
            />
            {/* Tooltip on hover */}
            <circle
              cx={point.x}
              cy={point.y}
              r="8"
              fill="transparent"
              className="hover:fill-opacity-50 cursor-pointer hover:fill-gray-100 dark:hover:fill-muted"
            >
              <title>
                {i18n.getCompletenessTrendText('percentComplete', {
                  value: point.value.toFixed(1),
                })}
              </title>
            </circle>
          </g>
        ))}

        {/* Y-axis labels */}
        <text
          x="10"
          y="55"
          fontSize="12"
          fill="#6b7280"
          className="dark:fill-muted-foreground"
          textAnchor="start"
        >
          100%
        </text>
        <text
          x="10"
          y="125"
          fontSize="12"
          fill="#6b7280"
          className="dark:fill-muted-foreground"
          textAnchor="start"
        >
          50%
        </text>
        <text
          x="10"
          y="195"
          fontSize="12"
          fill="#6b7280"
          className="dark:fill-muted-foreground"
          textAnchor="start"
        >
          0%
        </text>
      </svg>
    </div>
  );
}

/**
 * Enhanced trend indicator with more details
 */
function EnhancedTrendIndicator({
  direction,
  delta,
  current: _current,
  previous: _previous,
}: {
  direction: 'up' | 'down' | 'stable';
  delta: number;
  current: number;
  previous: number;
}) {
  const i18n = useDashboardTranslations();

  const getIcon = () => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="size-4" />;
      case 'down':
        return <TrendingDown className="size-4" />;
      default:
        return <Minus className="size-4" />;
    }
  };

  const getColor = () => {
    switch (direction) {
      case 'up':
        return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800';
      case 'down':
        return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-muted border-gray-200 dark:border-border';
    }
  };

  const formatDelta = (value: number) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  return (
    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${getColor()}`}>
      {getIcon()}
      <div className="flex flex-col">
        <span className="text-sm font-semibold">{formatDelta(delta)}</span>
        <span className="text-xs opacity-75">{i18n.getCompletenessTrendText('vsLastPeriod')}</span>
      </div>
    </div>
  );
}

/**
 * Statistics grid component
 */
function StatsGrid({ data }: { data: CompletenessTrendData }) {
  const i18n = useDashboardTranslations();
  const { completeness } = data;

  if (!completeness || completeness.length === 0) {
    return null;
  }

  // Filter out invalid values (NaN, null, undefined) and ensure we have valid numbers
  const validCompleteness = completeness.filter(
    value => typeof value === 'number' && !isNaN(value) && isFinite(value)
  );

  if (validCompleteness.length === 0) {
    return null;
  }

  const max = Math.max(...validCompleteness);
  const min = Math.min(...validCompleteness);
  const avg = validCompleteness.reduce((sum, val) => sum + val, 0) / validCompleteness.length;
  const volatility = Math.sqrt(
    validCompleteness.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) /
      validCompleteness.length
  );

  const stats = [
    {
      label: i18n.getStatsText('peak'),
      value: `${max.toFixed(1)}%`,
      icon: Target,
      color: 'text-green-600 dark:text-green-400',
    },
    {
      label: i18n.getStatsText('low'),
      value: `${min.toFixed(1)}%`,
      icon: BarChart3,
      color: 'text-red-600 dark:text-red-400',
    },
    {
      label: i18n.getStatsText('average'),
      value: `${avg.toFixed(1)}%`,
      icon: Activity,
      color: 'text-blue-600 dark:text-blue-400',
    },
    {
      label: i18n.getStatsText('volatility'),
      value: `${volatility.toFixed(1)}%`,
      icon: Calendar,
      color: 'text-purple-600 dark:text-purple-400',
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3">
      {stats.map((stat, index) => (
        <div
          key={index}
          className="flex items-center gap-2 rounded-lg bg-gray-50 p-2 dark:bg-muted"
        >
          <stat.icon className={`size-4 ${stat.color}`} />
          <div className="flex flex-col">
            <span className="text-xs text-gray-600 dark:text-muted-foreground">{stat.label}</span>
            <span className="text-sm font-semibold dark:text-foreground">{stat.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Enhanced Completeness Trend Card Component
 */
export function CompletenessTrendCard({
  data,
  loading = false,
  className = '',
}: CompletenessTrendCardProps) {
  const i18n = useDashboardTranslations();

  if (loading) {
    return (
      <Card className={`h-full ${className}`}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base font-semibold">
            {i18n.getCompletenessTrendText('title')}
          </CardTitle>
          <Skeleton className="h-8 w-20" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-60 w-full rounded-lg" />
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fallback data if none provided
  const trendData = data || {
    dates: [],
    completeness: [],
    delta: 0,
    current: 0,
    trend_direction: 'stable' as const,
  };

  const { completeness, delta, current, trend_direction, dates } = trendData;
  const previous = completeness.length > 1 ? completeness[completeness.length - 2] : current;

  return (
    <Card className={`h-full ${className}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base font-semibold">
          {i18n.getCompletenessTrendText('title')}
        </CardTitle>
        <EnhancedTrendIndicator
          direction={trend_direction}
          delta={delta}
          current={current}
          previous={previous}
        />
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Enhanced trend chart - now the main focus */}
        <EnhancedTrendChart data={completeness} />

        {/* Statistics grid */}
        <StatsGrid data={trendData} />

        {/* Period info */}
        <div className="flex items-center justify-between border-t pt-2 text-xs text-gray-500 dark:border-border dark:text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="size-3" />
            <span>
              {i18n.getCompletenessTrendText('period.lastDays', {
                count: dates.length,
              })}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <BarChart3 className="size-3" />
            <span>
              {i18n.getCompletenessTrendText('period.dataPoints', {
                count: completeness.length,
              })}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default CompletenessTrendCard;
